import type { ApiClient } from "../api/client";
import { ApiError } from "../api/client";
import {
  CLOSE_POLICY,
  PROTOCOL,
  TICKET_PREFIX,
  type ClientFrame,
  type ConnectionStatus,
  type EventFrame,
  type ServerFrame,
  type SnapshotFrame,
} from "./types";

interface TicketResponse {
  ticket: string;
  expires_in: number;
  protocol: string;
}

export interface RealtimeHandlers {
  /**
   * A full picture of the floor. The client MUST REPLACE its state with this,
   * not merge into it — see the note on connect() below.
   */
  onSnapshot(frame: SnapshotFrame): void;
  onEvent(frame: EventFrame): void;
  onStatus(status: ConnectionStatus, detail?: string): void;
}

export interface RealtimeOptions {
  api: ApiClient;
  handlers: RealtimeHandlers;
  /** Overridable for tests. */
  now?: () => number;
  socketFactory?: (url: string, protocols: string[]) => WebSocket;
}

const MIN_BACKOFF = 1_000;
const MAX_BACKOFF = 30_000;

/**
 * The realtime connection.
 *
 * THE CONTRACT, and the reason this design exists:
 *
 *  1. `hello` is always the first frame; `snapshot` is always the first STATE
 *     frame, on every connection, even when it is empty.
 *  2. On `snapshot` the consumer REPLACES its call and agent maps. Anything it
 *     was holding that the snapshot omits is gone. A call that ended while the
 *     socket was down is exactly the ghost the old system suffered from, and
 *     replacing rather than merging is what makes that impossible.
 *  3. `seq` is monotonic per connection, starting at 1 after the snapshot's 0.
 *     Gaps cannot happen: the gateway CLOSES a slow consumer rather than
 *     dropping a frame. So a gap means the connection is broken — reconnect.
 *  4. Never render state carried across a close without a new snapshot. If
 *     reconnection fails the UI shows disconnected; it does not keep drawing
 *     the last known calls as though they were live.
 */
export class RealtimeClient {
  #ws: WebSocket | null = null;
  #backoff = MIN_BACKOFF;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #pinger: ReturnType<typeof setInterval> | null = null;
  #lastFrameAt = 0;
  #seq = 0;
  #stopped = false;
  #pingIntervalMs = 20_000;

  constructor(private readonly opts: RealtimeOptions) {}

  get lastFrameAt(): number {
    return this.#lastFrameAt;
  }

  start(): void {
    this.#stopped = false;
    void this.#connect();
  }

  stop(): void {
    this.#stopped = true;
    this.#clearTimers();
    this.#ws?.close(1000, "client stopping");
    this.#ws = null;
  }

  send(frame: ClientFrame): void {
    if (this.#ws?.readyState === WebSocket.OPEN) {
      this.#ws.send(JSON.stringify(frame));
    }
  }

  async #connect(): Promise<void> {
    if (this.#stopped) return;
    this.opts.handlers.onStatus(this.#backoff === MIN_BACKOFF ? "connecting" : "reconnecting");

    let ticket: TicketResponse;
    try {
      ticket = await this.opts.api.post<TicketResponse>("/api/realtime/ws/ticket");
    } catch (e) {
      // A capability refusal is TERMINAL. Without this the client hammers the
      // ticket endpoint forever — the exact retry storm the old dashboard had
      // to add a module-level flag to stop.
      if (e instanceof ApiError && e.isForbidden) {
        this.opts.handlers.onStatus("forbidden", e.message);
        this.#stopped = true;
        return;
      }
      this.#scheduleReconnect();
      return;
    }

    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/realtime/ws/socket`;
    const protocols = [PROTOCOL, TICKET_PREFIX + ticket.ticket];

    let ws: WebSocket;
    try {
      ws = this.opts.socketFactory
        ? this.opts.socketFactory(url, protocols)
        : new WebSocket(url, protocols);
    } catch {
      this.#scheduleReconnect();
      return;
    }
    this.#ws = ws;
    this.#seq = 0;

    ws.onopen = () => {
      this.#lastFrameAt = this.#now();
    };

    ws.onmessage = (ev) => {
      this.#lastFrameAt = this.#now();
      let frame: ServerFrame;
      try {
        frame = JSON.parse(String(ev.data)) as ServerFrame;
      } catch {
        return;
      }
      this.#handle(frame);
    };

    ws.onerror = () => {
      /* onclose always follows; handle it there */
    };

    ws.onclose = (ev) => {
      this.#clearTimers();
      this.#ws = null;
      if (this.#stopped) return;
      if (ev.code === CLOSE_POLICY) {
        this.opts.handlers.onStatus("forbidden", ev.reason);
        this.#stopped = true;
        return;
      }
      this.#scheduleReconnect(ev.reason);
    };
  }

  #handle(frame: ServerFrame): void {
    switch (frame.type) {
      case "hello":
        this.#pingIntervalMs = frame.ping_interval_ms || 20_000;
        this.#startHeartbeat();
        break;

      case "snapshot":
        // The connection is only LIVE once state is coherent. Reset the backoff
        // here rather than on open: a socket that opens and immediately dies
        // must not reset it, or a flapping gateway is hammered.
        this.#backoff = MIN_BACKOFF;
        this.#seq = frame.seq;
        this.opts.handlers.onSnapshot(frame);
        this.opts.handlers.onStatus("live");
        break;

      case "event":
        if (frame.seq <= this.#seq) return; // replay of pre-snapshot history
        this.#seq = frame.seq;
        this.opts.handlers.onEvent(frame);
        break;

      case "shutdown":
        // A planned close. onclose follows; the reason just makes the log honest.
        this.opts.handlers.onStatus("reconnecting", frame.reason);
        break;

      case "ack":
      case "pong":
      case "error":
        break;
    }
  }

  /**
   * Heartbeat. Browser JS cannot send or observe WebSocket control frames, so
   * the server's pings are invisible here — an application-level ping is the
   * only way a client can detect a silently dead TCP connection, which
   * otherwise shows a frozen wallboard with no event to react to.
   */
  #startHeartbeat(): void {
    if (this.#pinger) clearInterval(this.#pinger);
    this.#pinger = setInterval(() => {
      const silence = this.#now() - this.#lastFrameAt;
      if (silence > this.#pingIntervalMs * 3) {
        this.opts.handlers.onStatus("stale", "no traffic; reconnecting");
        this.#ws?.close(4000, "heartbeat timeout");
        return;
      }
      this.send({ type: "ping" });
    }, this.#pingIntervalMs);
  }

  #scheduleReconnect(detail?: string): void {
    if (this.#stopped) return;
    this.opts.handlers.onStatus("reconnecting", detail);

    // Full jitter. Without it every dashboard in the building reconnects in
    // lockstep after a gateway restart and knocks it over again.
    const wait = Math.random() * this.#backoff;
    this.#backoff = Math.min(this.#backoff * 2, MAX_BACKOFF);
    this.#timer = setTimeout(() => void this.#connect(), wait);
  }

  #clearTimers(): void {
    if (this.#timer) clearTimeout(this.#timer);
    if (this.#pinger) clearInterval(this.#pinger);
    this.#timer = this.#pinger = null;
  }

  #now(): number {
    return this.opts.now ? this.opts.now() : Date.now();
  }
}
